<template>
  <div class="modal fade show d-block" tabindex="-1" role="dialog" aria-modal="true">
    <div class="modal-dialog modal-lg">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">
            Devolución
            <template v-if="venta?.cabecera?.nro_factura">
              — factura #{{ venta.cabecera.nro_factura }}
            </template>
          </h5>
          <button type="button" class="btn-close" aria-label="Close" @click="cerrar"></button>
        </div>
        <div class="modal-body">
          <div v-if="error" class="alert alert-danger">{{ error }}</div>

          <div class="table-responsive mb-3">
            <table class="table table-sm table-bordered align-middle">
              <thead class="table-light">
                <tr>
                  <th>Producto</th>
                  <th class="text-end">Vendido</th>
                  <th style="width: 140px">Cantidad a devolver</th>
                  <th class="text-center">Apto para reventa</th>
                  <th style="width: 160px">Vencimiento (si aplica)</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="fila in filas" :key="fila.producto_id" :class="{ 'text-muted': !fila.devolvible }">
                  <td>
                    {{ fila.nombre }}
                    <div v-if="!fila.devolvible" class="small text-danger">
                      No devolvible: es una presentación/combo, no soportado en esta versión.
                    </div>
                  </td>
                  <td class="text-end">{{ fila.cantidadVendidaDisplay }} {{ fila.unidadDisplay }}</td>
                  <td>
                    <input
                      type="number"
                      class="form-control form-control-sm"
                      min="0"
                      :max="fila.maxDisplay"
                      :step="fila.isBulk ? 0.001 : 1"
                      v-model.number="fila.cantidadDevolverInput"
                      :disabled="!fila.devolvible || enviando"
                      :data-testid="`item-devolucion-${fila.producto_id}`"
                    />
                  </td>
                  <td class="text-center">
                    <input
                      type="checkbox"
                      v-model="fila.aptoParaVenta"
                      :disabled="!fila.devolvible || enviando"
                      aria-label="Apto para reventa"
                    />
                  </td>
                  <td>
                    <input
                      v-if="fila.aptoParaVenta"
                      type="date"
                      class="form-control form-control-sm"
                      v-model="fila.fechaVencimiento"
                      :disabled="!fila.devolvible || enviando"
                    />
                    <span v-else class="text-muted small">—</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="row g-2">
            <div class="col-md-6">
              <label class="form-label">Motivo</label>
              <select class="form-select" v-model="motivoNegocio" :disabled="enviando">
                <option value="" disabled>Seleccionar motivo…</option>
                <option v-for="m in motivos" :key="m.value" :value="m.value">{{ m.label }}</option>
              </select>
            </div>
            <div class="col-md-6">
              <label class="form-label">Detalle (opcional)</label>
              <input type="text" class="form-control" v-model="motivoDetalle" :disabled="enviando" maxlength="250" />
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="cerrar" :disabled="enviando">Cancelar</button>
          <button
            type="button"
            class="btn btn-warning"
            :disabled="!puedeConfirmar || enviando"
            @click="confirmar"
          >
            <span v-if="enviando" class="spinner-border spinner-border-sm me-1"></span>
            Confirmar devolución
          </button>
        </div>
      </div>
    </div>
  </div>
  <div class="modal-backdrop fade show"></div>
</template>

<script>
import devolucionesService from '@/services/devolucionesService'
import { MOTIVOS_DEVOLUCION } from '@/constants/devolucionMotivos'

const isBulkDetalle = (producto) =>
  producto?.origen_lectura === 'BALANZA' || producto?.origen_lectura === 'MANUAL_GRANEL'

export default {
  name: 'DevolucionModal',
  props: {
    venta: {
      type: Object,
      required: true
    }
  },
  emits: ['cerrado', 'confirmada'],
  data() {
    return {
      motivos: MOTIVOS_DEVOLUCION,
      motivoNegocio: '',
      motivoDetalle: '',
      filas: this.construirFilas(),
      enviando: false,
      error: ''
    }
  },
  computed: {
    puedeConfirmar() {
      if (!this.motivoNegocio) return false
      return this.filas.some((f) => f.devolvible && this.cantidadRaw(f) > 0)
    }
  },
  methods: {
    construirFilas() {
      const detalles = this.venta?.detalles_productos || []
      return detalles.map((producto) => {
        const isBulk = isBulkDetalle(producto)
        const cantidadVendidaRaw = Number(producto.peso_gramos ?? producto.cantidad ?? 0)
        return {
          producto_id: producto.producto_id,
          nombre: producto.nombre_producto || producto.descripcion || `Producto ${producto.producto_id}`,
          isBulk,
          cantidadVendidaRaw,
          cantidadVendidaDisplay: isBulk ? (cantidadVendidaRaw / 1000).toFixed(3) : cantidadVendidaRaw,
          unidadDisplay: isBulk ? 'kg' : (producto.unidad_medida || ''),
          maxDisplay: isBulk ? Number((cantidadVendidaRaw / 1000).toFixed(3)) : cantidadVendidaRaw,
          cantidadDevolverInput: 0,
          aptoParaVenta: true,
          fechaVencimiento: null,
          devolvible: !producto.presentacion_id
        }
      })
    },
    // Cantidad en la unidad CRUDA que espera el backend (gramos para a granel,
    // unidades para el resto) — ver SPEC_DEVOLUCIONES_v0.1.md RF2-bis.
    cantidadRaw(fila) {
      const input = Number(fila.cantidadDevolverInput) || 0
      if (input <= 0) return 0
      return fila.isBulk ? Math.round(input * 1000) : Math.round(input)
    },
    cerrar() {
      this.$emit('cerrado')
    },
    async confirmar() {
      this.error = ''
      const items = this.filas
        .filter((f) => f.devolvible && this.cantidadRaw(f) > 0)
        .map((f) => ({
          producto_id: f.producto_id,
          cantidad: this.cantidadRaw(f),
          apto_para_venta: f.aptoParaVenta,
          fecha_vencimiento: f.aptoParaVenta && f.fechaVencimiento ? f.fechaVencimiento : null
        }))

      if (!items.length || !this.motivoNegocio) return

      this.enviando = true
      try {
        const resultado = await devolucionesService.crear({
          id_venta: this.venta.cabecera.id_venta,
          motivo_negocio: this.motivoNegocio,
          motivo_detalle: this.motivoDetalle.trim() || null,
          items
        })
        this.$emit('confirmada', resultado)
      } catch (err) {
        this.error =
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          err?.message ||
          'Error al registrar la devolución'
      } finally {
        this.enviando = false
      }
    }
  }
}
</script>
